// Inbounds (2026-10-29/30, docs/INBOUND.md): the shapes the backend (backend/inbound.py) answers with, shared by the
// list, the scanning screen, the detail and the replenish screen.
import { authFetch, API } from '../api';

export interface InboundRow {
  id: number; webshop_id: string; source: 'wgr_mail' | 'upload'; wgr_order_id: string | null; supplier_name: string | null;
  reference: string | null; status: 'open' | 'scanning' | 'finished' | 'done' | 'archived'; station: string | null;
  created_by: string | null; created_at: string | null; scanning_started_at: string | null; checked_at: string | null;
  done_at: string | null; archived_at: string | null; short_approved_by: string | null; short_approved_at: string | null;
  sync_fetched: number; booked_at: string | null; booked_by: string | null; booked_note: string | null;
  line_count?: number; products?: number; ordered?: number; scanned?: number; replenished?: number; short_lines?: number;
  arrived_at?: string | null; arrived_boxes?: string | null; arrivals?: number; boxes_expected?: number | null; boxes_expected_by?: string | null;
  waiting_orders?: number; waiting_pieces?: number; wgr_created_at?: string | null; ordered_on?: string | null;
  files?: number; invoice?: string | null;
  /** the worker's Finished, a manager's Confirm or Send back (1.11) */
  finished_by?: string | null; finished_at?: string | null;
  finished_note?: { all_cartons?: boolean; cartons_expected?: number | null; cartons_arrived?: number[]; short_lines?: number } | null;
  confirmed_by?: string | null; confirmed_at?: string | null; sent_back_by?: string | null; sent_back_at?: string | null; sent_back_note?: string | null;
  /** "Start replenishing" pressed on the laptop — only then a cart tablet is offered the inbound (1.11) */
  replenish_started_at?: string | null; replenish_started_by?: string | null;
}
export interface Arrival { id: number; arrived_at: string; boxes: number; note: string | null; marked_by: string | null; device: string | null }
/** a file on the inbound — the supplier's invoice first (31.10) */
export interface InboundFile { id: number; inbound_id: number; kind: 'invoice' | 'other'; filename: string; mime: string; size: number; note: string | null; uploaded_by: string | null; uploaded_at: string | null }
export interface InboundLine {
  id: number; kind: 'ordered' | 'extra'; article_number: string | null; ean: string | null; name: string | null; size: string | null;
  product_ref: any; style_key: string; box_no: number | null; box_id: number | null; box_label: string | null; stock_location: string | null;
  qty_ordered: number; qty_scanned: number; qty_replenished: number; short: boolean; ongoing: number; total_scanned: number;
  size_rank: number | null; waiting?: number;
  /** more than ordered, or not on the order: "Checked — N came" before its pieces go on a cart (1.11) */
  needs_check?: boolean; verified_qty?: number | null; verified_by?: string | null; verified_at?: string | null;
}
export interface InboundBox {
  id: number; box_no: number | null; special: 'Ä' | 'Ö' | null; label: string; boxes: number; style_key: string; stock_location: string | null;
  status: 'filling' | 'complete' | 'aside' | 'on_cart' | 'replenished'; trip_id: number | null; to_stock?: boolean;
  replenished_at: string | null; replenished_by: string | null; replenish_confirm: string | null; replenished_to: string | null;
  /** what is in it now and whether a cart tablet may take it (1.11) */
  pieces?: number; ready?: boolean; needs_check?: boolean; unknown?: boolean; waiting?: number; unsaved?: number;
  trip?: { trip_no: number; person: string | null; name: string | null; tablet: string | null; phase: string } | null;
}
export interface Group {
  style_key: string; name: string | null; labels: string[]; boxes: number; ordered: number; scanned: number; waiting?: number;
  line_ids: number[]; size_min: string | null; size_max: string | null; sizes: string[];
}
export interface Stop { box_id: number; box_no: number | null; label: string; boxes: number; section: string; order: number; style_key?: string; special?: string; no_location?: boolean; to_stock?: boolean }
export interface Trip {
  id?: number; trip_no: number; aisle?: string; aisle_name?: string; box_nos: number[]; box_ids: number[]; stops: Stop[];
  labels?: string[]; load_order?: (string | number)[]; started_at?: string | null; finished_at?: string | null;
  tablet?: string | null; person?: string | null; phase?: 'loading' | 'walking' | 'done'; loaded_at?: string | null; paused_at?: string | null;
}
export interface InboundSettings {
  boxes_per_station: number; cart_boxes: number; replenish_confirm: 'tap' | 'scan'; mail_reader: boolean; mail_query: string; sync_cap: number;
  manager_mode_minutes: number;
  /** WGR's supplier-order list — step 1 of the booking the push mail and the API give (2.11) */
  wgr_supplier_orders_url: string;
}
/** a manager's badge once at a station → barcode edits without a PIN per edit, until it is ended or runs out */
export interface ManagerMode { username: string; expires_at: string | null; minutes_left: number }
export interface MoveRow { id: number; box_id: number | null; style_key: string; from_display: string | null; to_display: string | null; status: string; started_by: string | null; count_differs: boolean }
export interface Detail {
  inbound: InboundRow; lines: InboundLine[]; groups: Group[]; boxes: InboundBox[]; trips: Trip[]; moves?: MoveRow[]; arrivals?: Arrival[]; files?: InboundFile[];
  /** the orders sitting in "NOS item didn't arrive" because of this supplier order */
  missing_orders?: { id: string; order_number: string; webshop_id: string }[]; open_session_id: number | null;
  totals: { ordered: number; scanned: number; replenished: number; short_lines: number; short_now?: number; to_check?: number;
            ready_boxes?: number; on_cart_boxes?: number; at_station_boxes?: number; trips_out?: number;
            still_to_replenish?: number; saved_pieces?: number };
  settings?: InboundSettings;
  /** the push "done — book it in WGR" (notification rule event inbound_done, 2.11) */
  booking_mail?: { status: string; recipients: string[]; sent_at: string | null; error: string | null; next_attempt_at: string | null } | null;
}
/** what /scan answers (status ok | over | extra | unknown | no_box | ambiguous) */
export interface ScanReply {
  status: string; barcode?: string; line_id?: number; box_no?: number | null; box_id?: number | null; box_label?: string | null; boxes?: number;
  article_number?: string | null; name?: string | null; size?: string | null; kind?: string; style_key?: string;
  size_min?: string | null; size_max?: string | null; style_ordered?: number; style_scanned?: number; waiting?: number;
  ongoing?: number; total_scanned?: number; qty_ordered?: number; message?: string; candidates?: any[];
  /** the piece asks for "Checked — N came"; the style's previous box went on a cart (→ this is a NEW box) */
  needs_check?: boolean; left_box?: string | null;
}

/** a cart tablet's trip (1.11): where to load, the loading order, the stops with what is in each box */
export interface TabletStop extends Stop {
  done: boolean; to: string | null; pieces: number; items: { article_number: string; name: string | null; size: string | null; pieces: number }[];
  move: { id: number; box_id: number; status: string } | null;
}
export interface TripView {
  trip: { id: number; trip_no: number; inbound_id: number; aisle: string; phase: 'loading' | 'walking' | 'done'; person: string; name: string;
          lang: string; tablet: string; paused: boolean; load_order: string[]; started_at: string | null; empty_checked?: boolean };
  inbound: { id: number; wgr_order_id: string | null; supplier_name: string | null };
  station: string; stops: TabletStop[]; next_stop: number | null; all_done: boolean;
  /** loading: the inbound's other ready boxes for this aisle pair — the person may take them too (2.11) */
  more_in_aisle?: (Stop & { pieces: number })[];
}
/** an inbound with boxes a cart tablet may take now */
export interface ReadyInbound { inbound_id: number; wgr_order_id: string | null; supplier_name: string | null; station: string; boxes: number; pieces: number; waiting: number; special: string[] }

export const STATUS_TEXT: Record<InboundRow['status'], string> = {
  open: 'Not started', scanning: 'Scanning and replenishing', finished: "Finished — waiting for a manager's check",
  done: 'Done — on the shelf', archived: 'Archived',
};

export async function getDetail(id: number | string): Promise<Detail> {
  const r = await authFetch(`${API}/inbound/${id}`);
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
}

/** POST with a JSON body; the Response comes back untouched so the caller can read 401 badge_required / 428 / 409. */
export function post(path: string, body: any = {}): Promise<Response> {
  return authFetch(`${API}/inbound${path}`, { method: 'POST', body: JSON.stringify(body) });
}

export async function detailOf(r: Response, fallback = 'Something went wrong'): Promise<string> {
  const d = await r.json().catch(() => ({} as any));
  if (typeof d.detail === 'string') return d.detail;
  if (d.detail?.message) return d.detail.message;
  return fallback;
}

export const when = (iso: string | null | undefined) => (iso ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : '');

/** The row colour of a line, as on the returns desk: red = more than ordered (or short after the manager's Confirm),
 *  green = complete, amber = pieces still expected. */
export function rowTone(l: InboundLine): string {
  const total = l.total_scanned;
  if (l.short) return 'bg-red-900/30';
  if (l.kind === 'extra') return total > 0 ? 'bg-red-900/30' : '';
  if (total === 0) return '';
  if (total > l.qty_ordered) return 'bg-red-900/30';
  if (total === l.qty_ordered) return 'bg-green-900/30';
  return 'bg-amber-900/25';
}

/** The chip / stop text of a box: "Ä", "Ö" or its number, with "×3" when several grey boxes carry the number. */
export const boxText = (b: { label?: string | null; special?: string | null; box_no?: number | null; boxes?: number }) =>
  `${b.label ?? b.special ?? (b.box_no ?? '—')}${(b.boxes || 1) > 1 ? ` ×${b.boxes}` : ''}`;
